import type { SkillCommand } from './skill-constants';

const SKILL_COMMAND_PATTERN = /(^|\s)\/([^\s]+)/g;

export interface SkillCommandMatch {
  command: SkillCommand;
  startIndex: number;
  endIndex: number;
}

export interface ActiveSkillQuery {
  query: string;
  startIndex: number;
  endIndex: number;
}

export function stripLeadingSlash(command?: string | null): string {
  return command?.trim().replace(/^\/+/, '') ?? '';
}

export function normalizeSkillCommand(command?: string | null): string {
  return stripLeadingSlash(command).toLowerCase();
}

export function suggestSkillCommand(command?: string | null): string {
  return normalizeSkillCommand(command)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function formatSkillCommand(command?: string | null): string {
  const normalized = stripLeadingSlash(command);
  return normalized ? `/${normalized}` : '';
}

export function toSkillDisplayName(command?: string | null): string {
  const normalized = stripLeadingSlash(command).trim();

  if (!normalized) {
    return 'Custom Skill';
  }

  return normalized
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function hasValidSkillCommandBoundary(text: string, handle: string, startIndex = 0): boolean {
  const handleWithSlash = `/${handle}`;
  if (startIndex < 0 || text.slice(startIndex, startIndex + handleWithSlash.length) !== handleWithSlash) {
    return false;
  }

  const beforeHandle = startIndex === 0 ? '' : text[startIndex - 1];
  if (beforeHandle && !/\s/.test(beforeHandle)) {
    return false;
  }

  const afterHandle = text.slice(startIndex + handleWithSlash.length);
  return afterHandle === '' || afterHandle.startsWith(' ');
}

export function findActiveSkillQuery(text: string, cursorPosition = text.length): ActiveSkillQuery | null {
  const safeCursorPosition = Math.max(0, Math.min(cursorPosition, text.length));
  let startIndex = safeCursorPosition;

  while (startIndex > 0 && !/\s/.test(text[startIndex - 1] ?? '')) {
    startIndex -= 1;
  }

  let endIndex = safeCursorPosition;
  while (endIndex < text.length && !/\s/.test(text[endIndex] ?? '')) {
    endIndex += 1;
  }

  const query = text.slice(startIndex, endIndex);
  if (!query.startsWith('/')) {
    return null;
  }

  return {
    query,
    startIndex,
    endIndex,
  };
}

export function findMatchingSkillCommand(
  text: string,
  commands: SkillCommand[],
  cursorPosition = text.length,
): SkillCommand | null {
  const activeQuery = findActiveSkillQuery(text, cursorPosition);
  if (!activeQuery) {
    return null;
  }

  const typedHandle = activeQuery.query.slice(1);
  const command = commands.find((item) => item.handle === typedHandle);

  if (command && hasValidSkillCommandBoundary(text, command.handle, activeQuery.startIndex)) {
    return command;
  }

  return null;
}

export function findFirstMatchingSkillCommand(text: string, commands: SkillCommand[]): SkillCommandMatch | null {
  for (const match of text.matchAll(SKILL_COMMAND_PATTERN)) {
    const handle = match[2];
    const command = commands.find((item) => item.handle === handle);
    if (!command) {
      continue;
    }

    const prefixLength = (match[1] ?? '').length;
    const fullMatch = match[0] ?? '';
    const startIndex = (match.index ?? 0) + prefixLength;
    const endIndex = startIndex + fullMatch.length - prefixLength;

    if (hasValidSkillCommandBoundary(text, command.handle, startIndex)) {
      return {
        command,
        startIndex,
        endIndex,
      };
    }
  }

  return null;
}

export function isSkillCommandDisabledByAccount(command: SkillCommand, hasAccountInContext: boolean): boolean {
  return Boolean(command.accountRequired) && !hasAccountInContext;
}

export function findNextSelectableSkillCommandIndex(
  commands: SkillCommand[],
  currentIndex: number,
  delta: number,
  isCommandDisabled: (command: SkillCommand) => boolean,
): number {
  if (commands.length === 0 || delta === 0) {
    return -1;
  }

  const direction = delta > 0 ? 1 : -1;
  let nextIndex = currentIndex < 0 ? (direction > 0 ? 0 : commands.length - 1) : currentIndex;

  for (let offset = 0; offset < commands.length; offset += 1) {
    if (currentIndex >= 0 || offset > 0) {
      nextIndex = (nextIndex + direction + commands.length) % commands.length;
    }

    const command = commands[nextIndex];
    if (command && !isCommandDisabled(command)) {
      return nextIndex;
    }
  }

  return -1;
}

export function renderSkillCommandContent(text: string, matchedCommand: SkillCommandMatch | null): string {
  if (!matchedCommand) {
    return escapeText(text, true);
  }

  const handle = `/${matchedCommand.command.handle}`;
  const before = text.slice(0, matchedCommand.startIndex);
  const rest = text.slice(matchedCommand.startIndex + handle.length);
  const hasExtraText = rest.trim().length > 0;
  const escapedPlaceholder = escapeText(matchedCommand.command.instructionPlaceholder ?? '');
  const placeholderHtml =
    matchedCommand.startIndex === 0 && !hasExtraText && escapedPlaceholder
      ? `<span class="text-steel-300 pointer-events-none select-none" contenteditable="false" data-instruction-placeholder="true">${escapedPlaceholder}</span>`
      : '';

  return `${escapeText(before, true)}<span class="text-brand-300">${escapeText(handle)}</span>${escapeText(rest, true)}${placeholderHtml}`;
}

export function replaceActiveSkillQuery(text: string, handle: string, cursorPosition = text.length) {
  const replacement = `/${handle}`;
  const activeQuery = findActiveSkillQuery(text, cursorPosition);

  if (!activeQuery) {
    const suffix = text.slice(cursorPosition);
    const separator = suffix.startsWith(' ') || suffix.startsWith('\n') ? '' : ' ';
    const cursorOffset = separator ? separator.length : suffix ? 1 : 0;
    return {
      nextValue: `${text.slice(0, cursorPosition)}${replacement}${separator}${suffix}`,
      nextCursorPosition: cursorPosition + replacement.length + cursorOffset,
      commandStartIndex: cursorPosition,
      commandEndIndex: cursorPosition + replacement.length,
    };
  }

  const suffix = text.slice(activeQuery.endIndex);
  const separator = suffix.startsWith(' ') || suffix.startsWith('\n') ? '' : ' ';
  const cursorOffset = separator ? separator.length : suffix ? 1 : 0;

  return {
    nextValue: `${text.slice(0, activeQuery.startIndex)}${replacement}${separator}${suffix}`,
    nextCursorPosition: activeQuery.startIndex + replacement.length + cursorOffset,
    commandStartIndex: activeQuery.startIndex,
    commandEndIndex: activeQuery.startIndex + replacement.length,
  };
}

export function startsWithSkillCommand(text: string): boolean {
  return /^\/[\w-]+(?:\s|$)/.test(text);
}

function escapeText(text: string, preserveNewlines = false): string {
  const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return preserveNewlines ? escapedText.replace(/\n/g, '<br>') : escapedText;
}
